/**
 * One dialog, two geometries (D1's `ui/` primitives).
 *
 * Two drawers were built by hand first — `Journal.tsx` and `CharacterPanel.tsx` — and they agreed
 * on Escape, on the backdrop, on the header and on focus by copy. three more arrived with the peripheral surfaces
 * (delete, feedback, and whatever the world codex's world modal turns out to be), and five hand-rolled
 * dialogs is five places for the keyboard path to be subtly different. So the
 * behaviour moves here and the callers keep only their contents.
 *
 * What the primitive owns, and why each one is here rather than in a caller:
 *
 * - **Escape, and a backdrop that is not a control.** Announcing a full-viewport click target
 * is noise; the dialog already has two ways out, both reachable from a keyboard.
 * - **Focus in, and focus *back*.** The element that was focused when the dialog opened is
 * restored on close, so a keyboard reader returns to the control they came from rather than
 * to the top of the document with the story somewhere below them. Callers used to do this
 * with a ref to their own toggle; capturing `document.activeElement` here means a caller
 * that forgets cannot get it wrong.
 * - **A Tab trap.** `aria-modal` tells a screen reader the rest of the page is inert; it does
 * nothing to Tab, so without this the fifth Tab lands on the story feed behind the backdrop.
 * The accessibility work owns the full keyboard path, but a modal that leaks focus is not a
 * thing to defer.
 * - **The accessible name is the visible title.** One string, so they cannot drift apart.
 * - **And it renders at `document.body`, not where it is written.** This one was found the hard
 *   way. The world panel and the journal are written inside the play screen's `<nav>`, which
 *   carries `backdrop-blur sm:backdrop-blur-none` — and Tailwind v4's `backdrop-blur-none` does
 *   *not* clear the property, it sets `--tw-backdrop-blur: " "` and re-emits the
 *   `backdrop-filter: var(--tw-backdrop-blur,) …` chain. So the nav established a stacking
 *   context at every width, the `z-30` below collapsed into it, and the story screen's sticky
 *   foot — `position: sticky` is a stacking context unconditionally, and it comes later in tree
 *   order — painted the action bar straight over the open drawer. The player could not read the
 *   bottom half of the codex.
 *
 *   The portal is the fix that does not depend on knowing that: a modal must not care where in
 *   the tree it was written, and the bug *was* that dependency. `z-20`/`z-30` now mean what they
 *   say against the root stacking context, and no caller can reintroduce the trap by adding a
 *   filter to one of its own ancestors.
 */
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useT } from '@/i18n/context';

export type DialogVariant = 'center' | 'drawer';

interface DialogProps {
  open: boolean;
  onClose: () => void;
  /** Rendered in the header *and* used as the accessible name. */
  title: string;
  /** `drawer` for the panels that live at an edge, `center` for a decision. */
  variant?: DialogVariant;
  /** Drawers only. Ignored by `center`. */
  side?: 'left' | 'right';
  children: ReactNode;
  /** The dialog's actions, right-aligned under a rule. Omitted for a panel that only reads. */
  footer?: ReactNode;
}

export function Dialog({ open, ...props }: DialogProps) {
  // Mounted only while open, so every effect below can key off mount rather than off a
  // boolean — which is what makes "restore focus on close" a plain cleanup.
  return open ? <DialogShell {...props} /> : null;
}

function DialogShell({
  onClose,
  title,
  variant = 'center',
  side = 'right',
  children,
  footer,
}: Omit<DialogProps, 'open'>) {
  const t = useT();
  const panel = useRef<HTMLDivElement | null>(null);
  const titleId = useId();

  useEffect(() => {
    const opener = document.activeElement;
    panel.current?.focus();

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key === 'Tab') trapTab(event, panel.current);
    };

    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (opener instanceof HTMLElement) opener.focus();
    };
  }, [onClose]);

  return createPortal(
    <>
      {/* Deliberately not a button: it is a backdrop, Escape and the close control are the
          keyboard paths out, and a click target this large announced as a control is noise.
          Same call `Journal.tsx` and `CharacterPanel.tsx` made before this file existed. */}
      <div
        aria-hidden="true"
        onClick={onClose}
        className="bg-bg/70 fixed inset-0 z-20 backdrop-blur-sm"
      />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={`border-border bg-panel fixed z-30 flex flex-col outline-none ${
          variant === 'drawer' ? DRAWER[side] : CENTER
        }`}
      >
        <div className="border-rule flex items-center justify-between border-b px-5 py-4">
          <h2
            id={titleId}
            className="text-fg font-mono tracking-wider"
            style={{ fontSize: 'var(--text-meta)' }}
          >
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-fg-faint hover:text-fg font-mono transition-colors"
            style={{ fontSize: 'var(--text-meta)' }}
          >
            {t('ui.common.close')}
          </button>
        </div>

        <div className="grow overflow-y-auto px-5 py-4">{children}</div>

        {footer ? (
          <div className="border-rule flex justify-end gap-3 border-t px-5 py-4">{footer}</div>
        ) : null}
      </div>
    </>,
    document.body,
  );
}

/**
 * **A bottom sheet on a phone, an edge drawer above `sm`.**
 *
 * One class string rather than a second variant, because the panels behind it are the same
 * panels: a full-height drawer sliding in from the left of a 390-pixel screen is a drawer that
 * *is* the screen, arriving from a direction that means nothing, with its close control at the
 * top where the thumb is not. A sheet that comes up from the bottom is the platform convention
 * on both mobile platforms, it leaves the last paragraph of the story visible above it, and
 * `max-h-[85dvh]` is what keeps that true.
 *
 * `dvh` rather than `vh` deliberately: mobile Safari's toolbar makes `vh` taller than the
 * visible viewport, so a sheet sized in `vh` puts its own footer under the browser chrome.
 */
const DRAWER = {
  left: 'inset-x-0 bottom-0 max-h-[85dvh] border-t sm:inset-y-0 sm:right-auto sm:left-0 sm:max-h-none sm:w-full sm:max-w-sm sm:border-t-0 sm:border-r',
  right:
    'inset-x-0 bottom-0 max-h-[85dvh] border-t sm:inset-y-0 sm:left-auto sm:right-0 sm:max-h-none sm:w-full sm:max-w-sm sm:border-t-0 sm:border-l',
} as const;

const CENTER =
  'top-1/2 left-1/2 max-h-[85dvh] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 border';

/**
 * `summary` is in the list because the character panel's sections and the DM notes are `<details>`, and a
 * disclosure the trap cannot see is a disclosure Tab escapes through.
 */
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'summary',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * Whether Tab would actually reach it. Deliberately not `offsetParent`/`checkVisibility`:
 * jsdom has no layout, so a geometry test says "invisible" about every control in the suite
 * and the trap would swallow every Tab. The one case that genuinely matters here is a
 * *closed* `<details>` — the character panel's sections and the DM notes are disclosures, and their contents
 * are in the DOM while being unreachable.
 */
function isReachable(element: HTMLElement): boolean {
  if (element.hidden) return false;
  if (element.tagName === 'SUMMARY') return true;
  const details = element.closest('details');
  return details === null || details.open;
}

function trapTab(event: KeyboardEvent, panel: HTMLElement | null): void {
  if (!panel) return;

  const focusable = [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(isReachable);
  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  // An empty dialog still must not hand focus back to the page behind it.
  if (!first || !last) {
    event.preventDefault();
    panel.focus();
    return;
  }

  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}
